/* ═══════════════════════════════════════════════════════════
   Denova vibe engine.
   One source of truth: html[data-vibe]. Everything reacts to it —
   CSS tokens, copy slots, the hero room, kit add-to-cart forms,
   and the real Shopify variant picker on kit product pages.
   Priority on load: ?vibe= URL param → localStorage → nightfall.
   ═══════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var STORAGE_KEY = 'denova:vibe';
  var DEFAULT_VIBE = 'nightfall';

  function readRegistry() {
    var el = document.getElementById('DenovaVibeData');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  var VIBES = null;

  function safeGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function safeSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) { /* private mode */ }
  }

  function initialVibe() {
    var fromUrl = new URLSearchParams(window.location.search).get('vibe');
    if (fromUrl && VIBES[fromUrl.toLowerCase()]) return fromUrl.toLowerCase();
    var stored = safeGet(STORAGE_KEY);
    if (stored && VIBES[stored]) return stored;
    return DEFAULT_VIBE;
  }

  function formatMoney(cents) {
    var n = cents / 100;
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  /* ── copy slots: <span data-dnv-word="word|word-lower|hex|sign|sign-desc|mat|mat-desc"> ── */
  function updateWords(v) {
    var map = {
      'word': v.word,
      'word-lower': v.word.toLowerCase(),
      'hex': v.hex,
      'sign': v.sign,
      'sign-desc': v.signDesc,
      'mat': v.mat,
      'mat-desc': v.matDesc
    };
    document.querySelectorAll('[data-dnv-word]').forEach(function (el) {
      var mode = el.getAttribute('data-dnv-word');
      if (map[mode] != null) el.textContent = map[mode];
    });
  }

  /* ── vibe picker buttons ── */
  function updateButtons(key) {
    document.querySelectorAll('[data-dnv-vibe-btn]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-v') === key));
    });
  }

  /* ── hero room: per-vibe render if provided, else the SVG fallback ── */
  function updateRooms(key) {
    document.querySelectorAll('.dnv-room').forEach(function (room) {
      var imgs = room.querySelectorAll('[data-dnv-room]');
      if (!imgs.length) return;
      var hasVibeImg = false;
      imgs.forEach(function (el) {
        var match = el.getAttribute('data-dnv-room') === key;
        if (match && el.tagName !== 'svg') hasVibeImg = true;
        el.classList.toggle('dnv-on', match);
      });
      var svg = room.querySelector('[data-dnv-room="svg"]');
      if (svg) svg.classList.toggle('dnv-on', !hasVibeImg);
    });
  }

  /* ── kit forms: swap the hidden variant id + price + label ── */
  function updateKitForms(key) {
    document.querySelectorAll('dnv-kit-form').forEach(function (kf) {
      kf.setVibe(key);
    });
  }

  /* ── kit PDP: drive the real Dawn variant picker ── */
  function findVibeInputs() {
    // Dawn renders radios/selects named after the option ("Vibe").
    var inputs = document.querySelectorAll(
      'variant-selects input[type="radio"][name="Vibe"], variant-radios input[type="radio"][name="Vibe"]'
    );
    if (inputs.length) return { type: 'radio', els: inputs };
    // some pickers (incl. this theme's PDP dropdown) put the variant id in the
    // option value and the vibe word in the option text — match either
    var selects = document.querySelectorAll('variant-selects select, variant-radios select, select[name^="options["]');
    for (var i = 0; i < selects.length; i++) {
      var opts = Array.prototype.map.call(selects[i].options, function (o) {
        return (o.value + ' ' + o.text).toLowerCase();
      });
      var known = Object.keys(VIBES).filter(function (k) {
        var word = VIBES[k].word.toLowerCase();
        return opts.some(function (t) { return t.indexOf(word) !== -1; });
      });
      if (known.length >= 2) return { type: 'select', els: [selects[i]] };
    }
    return null;
  }

  function optionMatchesWord(option, word) {
    return option.value.toLowerCase() === word || option.text.trim().toLowerCase() === word;
  }

  function syncPickerToVibe(key) {
    var picker = findVibeInputs();
    if (!picker) return;
    var word = VIBES[key].word.toLowerCase();
    if (picker.type === 'radio') {
      picker.els.forEach(function (input) {
        if (input.value.toLowerCase() === word && !input.checked && !input.disabled) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    } else {
      var sel = picker.els[0];
      for (var i = 0; i < sel.options.length; i++) {
        if (optionMatchesWord(sel.options[i], word)) {
          if (sel.selectedIndex !== i) {
            sel.selectedIndex = i;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
          break;
        }
      }
    }
  }

  function vibeKeyForOptionValue(value) {
    var lower = String(value).toLowerCase();
    for (var k in VIBES) {
      if (VIBES[k].word.toLowerCase() === lower) return k;
    }
    return null;
  }

  var applying = false;

  function setVibe(key, opts) {
    opts = opts || {};
    if (!VIBES[key] || applying) return;
    applying = true;
    var v = VIBES[key];
    document.documentElement.setAttribute('data-vibe', key);
    safeSet(STORAGE_KEY, key);
    updateButtons(key);
    updateWords(v);
    updateRooms(key);
    updateKitForms(key);
    if (!opts.fromPicker) syncPickerToVibe(key);
    document.dispatchEvent(new CustomEvent('denova:vibe', { detail: { vibe: key } }));
    applying = false;
  }

  /* ── kit add-to-cart form (same drawer flow as Dawn's product-form) ── */
  if (!customElements.get('dnv-kit-form')) {
    customElements.define('dnv-kit-form', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.idInput = this.form.querySelector('[name="id"]');
        this.button = this.form.querySelector('[type="submit"]');
        this.labelEl = this.querySelector('[data-dnv-vibe-label]');
        this.priceEl = this.querySelector('[data-dnv-kit-price]');
        this.strikeEl = this.querySelector('[data-dnv-kit-strike]');
        this.errorEl = this.querySelector('.dnv-kit-error');
        try {
          this.variants = JSON.parse(this.querySelector('[data-dnv-kit-variants]').textContent);
        } catch (e) {
          this.variants = {};
        }
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        var current = document.documentElement.getAttribute('data-vibe') || DEFAULT_VIBE;
        this.setVibe(current);
      }

      setVibe(key) {
        var v = this.variants[key];
        if (!this.idInput) return;
        if (!v) {
          // vibe not offered on this kit — keep current variant, note it
          if (this.button) this.button.setAttribute('aria-disabled', 'true');
          return;
        }
        this.idInput.value = v.id;
        if (this.labelEl && VIBES) this.labelEl.textContent = '— ' + VIBES[key].word;
        if (this.priceEl) this.priceEl.textContent = formatMoney(v.price);
        if (this.strikeEl) {
          if (v.compareAt && v.compareAt > v.price) {
            this.strikeEl.innerHTML =
              '<s>' + formatMoney(v.compareAt) + '</s> separately · <b>save ' +
              formatMoney(v.compareAt - v.price) + '</b>';
          } else {
            this.strikeEl.textContent = '';
          }
        }
        if (this.button) {
          if (v.available) {
            this.button.removeAttribute('disabled');
            this.button.removeAttribute('aria-disabled');
          } else {
            this.button.setAttribute('disabled', '');
            this.button.setAttribute('aria-disabled', 'true');
          }
        }
      }

      onSubmit(evt) {
        evt.preventDefault();
        if (this.button && this.button.hasAttribute('disabled')) return;
        var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        var body = new FormData(this.form);
        if (cart && cart.getSectionsToRender) {
          body.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
          body.append('sections_url', window.location.pathname);
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
        }
        var self = this;
        if (this.button) this.button.classList.add('loading');
        fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
          body: body
        })
          .then(function (r) { return r.json(); })
          .then(function (response) {
            if (response.status) {
              self.showError(response.description || response.message || 'Could not add to cart.');
              return;
            }
            self.showError(null);
            if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'dnv-kit-form',
                productVariantId: body.get('id'),
                cartData: response
              });
            }
            if (cart && cart.renderContents) {
              cart.classList.remove('is-empty');
              cart.renderContents(response);
            } else {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
            }
          })
          .catch(function () {
            self.showError('Something went wrong — try again.');
          })
          .finally(function () {
            if (self.button) self.button.classList.remove('loading');
          });
      }

      showError(msg) {
        if (!this.errorEl) return;
        if (msg) {
          this.errorEl.textContent = msg;
          this.errorEl.classList.add('dnv-show');
        } else {
          this.errorEl.classList.remove('dnv-show');
        }
      }
    });
  }

  /* ── reveal on scroll ── */
  function initReveals() {
    var nodes = document.querySelectorAll('.dnv-rv:not(.dnv-in)');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach(function (n) { n.classList.add('dnv-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('dnv-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  function boot() {
    VIBES = readRegistry();
    if (!VIBES) return;

    // kit PDP: give the product section the vibe glow (no liquid edit needed)
    if (findVibeInputs()) {
      var mainProduct = document.querySelector('section[id^="MainProduct-"]');
      if (mainProduct) mainProduct.classList.add('dnv-pdp-glow');
    }

    setVibe(initialVibe());

    // vibe buttons (event delegation survives section re-renders)
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-dnv-vibe-btn]');
      if (btn) setVibe(btn.getAttribute('data-v'));
    });

    // real variant picker → vibe (kit PDP)
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || applying) return;
      var key = null;
      if (t.matches && t.matches('input[type="radio"][name="Vibe"]')) {
        key = vibeKeyForOptionValue(t.value);
      } else if (t.tagName === 'SELECT' &&
                 (t.closest('variant-selects, variant-radios') || /^options\[/.test(t.name || ''))) {
        var opt = t.options[t.selectedIndex];
        key = vibeKeyForOptionValue(t.value) || (opt && vibeKeyForOptionValue(opt.text.trim()));
      }
      if (key) setVibe(key, { fromPicker: true });
    });

    initReveals();

    // theme editor: sections re-render, re-apply current state
    document.addEventListener('shopify:section:load', function () {
      setVibe(document.documentElement.getAttribute('data-vibe') || DEFAULT_VIBE);
      initReveals();
    });

    window.DenovaVibe = { set: setVibe, get: function () { return document.documentElement.getAttribute('data-vibe'); } };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
